import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart`, {
      description: 'View your cart to checkout',
      action: {
        label: 'View Cart',
        onClick: () => window.location.href = '/cart'
      }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        {/* Single Column Layout - Horizontal Card */}
        <div className="flex flex-col md:flex-row gap-6 bg-[#FAF8F5] border border-[#E8E4DE] hover:border-[#2D4A3E]/30 transition-all duration-500 overflow-hidden">
          {/* Image */}
          <div className="w-full md:w-80 lg:w-96 aspect-square md:aspect-[4/3] overflow-hidden flex-shrink-0 relative">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            
            {/* Badge */}
            {product.badge && (
              <span className={`absolute top-4 left-4 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold ${
                product.badge === 'Best Seller' 
                  ? 'bg-[#C67D4E] text-white' 
                  : product.badge === 'Premium'
                  ? 'bg-[#2D4A3E] text-white'
                  : 'bg-[#8B7355] text-white'
              }`}>
                {product.badge}
              </span>
            )}

            {product.isNew && !product.badge && (
              <span className="absolute top-4 left-4 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-[#2D4A3E] text-white">
                New
              </span>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 p-6 md:py-8 flex flex-col justify-between">
            <div>
              <p className="font-['Montserrat'] text-xs uppercase tracking-[0.15em] text-[#8B7355] mb-2">
                {product.category.replace('-', ' ')}
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#2D4A3E] group-hover:text-[#C67D4E] transition-colors leading-tight">
                {product.name}
              </h2>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mt-3">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={i < Math.floor(product.rating) ? 'fill-[#C67D4E] text-[#C67D4E]' : 'text-[#E8E4DE]'}
                    />
                  ))}
                </div>
                <span className="text-xs text-[#8B7355] font-['Montserrat']">({product.reviews} reviews)</span>
              </div>

              {/* Description preview */}
              <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mt-4 line-clamp-2 hidden md:block">
                {product.description}
              </p>
            </div>

            {/* Price & Actions */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#E8E4DE]">
              <div className="flex items-baseline gap-3">
                <span className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-semibold text-[#2D4A3E]">
                  ${product.price.toLocaleString()}
                </span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="font-['Montserrat'] text-sm text-[#8B7355] line-through">
                    ${product.originalPrice.toLocaleString()}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleAddToCart}
                  className="flex items-center gap-2 bg-[#2D4A3E] text-white px-5 py-3 text-xs uppercase tracking-wider font-semibold hover:bg-[#1E332A] transition-colors"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingBag size={16} />
                  <span className="hidden sm:inline">Add to Cart</span>
                </button>
                <span className="flex items-center gap-1 text-[#C67D4E] text-sm font-medium group-hover:gap-2 transition-all">
                  View <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Grid version for homepage sections
export const ProductCardGrid = ({ product, index = 0 }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group cursor-pointer"
      data-testid={`product-card-grid-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className="aspect-[3/4] overflow-hidden bg-[#FAF8F5] relative border border-[#E8E4DE]">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          
          {product.badge && (
            <span className={`absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold ${
              product.badge === 'Best Seller' 
                ? 'bg-[#C67D4E] text-white' 
                : 'bg-[#2D4A3E] text-white'
            }`}>
              {product.badge}
            </span>
          )}

          <button
            onClick={handleAddToCart}
            className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm text-[#2D4A3E] py-2.5 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#2D4A3E] hover:text-white"
            data-testid={`add-to-cart-grid-${product.id}`}
          >
            <ShoppingBag size={14} />
            Add to Cart
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <p className="font-['Montserrat'] text-[10px] uppercase tracking-[0.15em] text-[#8B7355]">
            {product.category.replace('-', ' ')}
          </p>
          <h3 className="font-['Cormorant_Garamond'] text-lg text-[#2D4A3E] group-hover:text-[#C67D4E] transition-colors leading-tight">
            {product.name}
          </h3>
          
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={12}
                className={i < Math.floor(product.rating) ? 'fill-[#C67D4E] text-[#C67D4E]' : 'text-[#E8E4DE]'}
              />
            ))}
            <span className="text-[10px] text-[#8B7355] ml-1">({product.reviews})</span>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2D4A3E]">
              ${product.price.toLocaleString()}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="font-['Montserrat'] text-xs text-[#8B7355] line-through">
                ${product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
